/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MatchModule */

import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    Type,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { SchemaService } from 'ajs/modules/core/services/schema-service';
import { DefaultValues } from 'ajs/modules/core/services/default-values.service';

import {
    IComponentAddOption,
    ISelectedComponent,
} from 'ng/modules/avi-component-rendering/components/avi-component-adder';

import * as globalL10n from 'global-l10n';
import * as l10n from './match-adder-with-objects.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type IComponentBindings = Record<string, unknown>;
type IMatchConfig = Record<string, unknown>;

/**
 * Interface of the match option object. Used to render the match component.
 */
export interface IMatchWithObjectsOption extends IComponentAddOption, ISelectedComponent {
    label: string;
    subLabel?: string;
    objectType?: string;
    fieldName?: string;
    component: Type<Component>;
    componentBindings?: IComponentBindings;
    defaultConfigOverride?: IMatchConfig;
}

/**
 * Additional properties added to the component bindings. These are passed to every match component.
 */
interface IExtendedComponentBindings extends IComponentBindings {
    label: string;
    subLabel: string;
    objectType: string;
    matchObjectType: string;
    config: IMatchConfig;
    onRemoveMatch(): void;
}

/**
 * A selected option has the same fields as a match option with additional fields added to componentBindings.
 */
interface ISelectedOption extends IMatchWithObjectsOption {
    componentBindings: IExtendedComponentBindings;
}

/**
 * @description
 *     This component is similar to MatchAdderComponent except MatchAdderComponent assumes the config contains message
 *     items, while MatchAdderWithObjectsComponent assumes the config contains regular objects and arrays. In other
 *     words, use MatchAdderWithObjectsComponent for Item instances and MatchAdderComponent for ObjectTypeItem
 *     instances.
 * @example
 *     <match-adder-with-objects
 *         [(match)]="config.match"
 *         matchObjectType="WafPSMLocationMatch"
 *         [matchOptions]="matchOptions"
 *         [disableAddButton]="preventEdit"
 *     ></match-adder-with-objects>
 * @author alextsg
 */
@Component({
    selector: 'match-adder-with-objects',
    templateUrl: './match-adder-with-objects.component.html',
})
export class MatchAdderWithObjectsComponent implements OnInit {
    /**
     * Parent match object containing child match configs.
     */
    @Input()
    public match: Record<string, any>;

    /**
     * Object type of the parent match object. Used to get protobuf info about child match configs.
     */
    @Input()
    public matchObjectType: string;

    /**
     * List of match options that the user can add. These options will be used to create the dropdown button options.
     */
    @Input()
    public matchOptions: IMatchWithObjectsOption[] = [];

    /**
     * Flag to disable the add button.
     */
    @Input()
    public disableAddButton = false;

    @Output()
    public matchChange = new EventEmitter<Record<string, any>>();

    /**
     * List of matches that can be added.
     */
    public selectableOptions: IMatchWithObjectsOption[] = [];

    /**
     * List of matches already selected.
     */
    public selectedOptions: ISelectedOption[] = [];

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    constructor(
        l10nService: L10nService,
        private readonly schemaService: SchemaService,
        private readonly defaultValuesService: DefaultValues,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        this.setSelectedOptions();
        this.setSelectableOptions();
    }

    /**
     * Add a match config and match component when the user has selected a match from the Add dropdown button.
     */
    public addMatch(dropdownAction: IMatchWithObjectsOption): void {
        const { fieldName } = dropdownAction;
        const matchObject = this.match || {};
        let config: IMatchConfig;

        if (this.isRepeatedMatch(fieldName)) {
            matchObject[fieldName] = matchObject[fieldName] || [];

            const repeatedMatchConfig = matchObject[fieldName];

            repeatedMatchConfig.push(this.getDefaultMatchConfig(fieldName, dropdownAction));

            config = repeatedMatchConfig[repeatedMatchConfig.length - 1];
        } else {
            matchObject[fieldName] = this.getDefaultMatchConfig(fieldName, dropdownAction);

            config = matchObject[fieldName];
        }

        this.addSelectedOption(dropdownAction, config);
        this.setSelectableOptions();
        this.matchChange.emit(matchObject);
    }

    /**
     * Delete the messageItem from the match messageItem and remove the associated match component.
     */
    private removeMatch(fieldName: string, config: IMatchConfig, selectedOptionIndex = 0): void {
        if (this.isRepeatedMatch(fieldName)) {
            const index = this.match[fieldName].indexOf(config);

            this.match[fieldName].splice(index, 1);

            if (!this.match[fieldName].length) {
                delete this.match[fieldName];
            }
        } else {
            delete this.match[fieldName];
        }

        this.selectedOptions = [
            ...this.selectedOptions.slice(0, selectedOptionIndex),
            ...this.selectedOptions.slice(selectedOptionIndex + 1),
        ];

        this.setSelectableOptions();
        this.matchChange.emit(this.match);
    }

    /**
     * Set the AviDropdownButton options. A match can only be selected if it wasn't already selected, or if it's a
     * repeated match.
     */
    private setSelectableOptions(): void {
        this.selectableOptions = this.matchOptions.filter(option => {
            const { fieldName } = option;

            if (!this.match) {
                return true;
            }

            return this.isRepeatedMatch(fieldName) || !Object.prototype.hasOwnProperty.call(this.match, fieldName);
        });
    }

    /**
     * Create a selected option object by extending componentBindings for rendering a match component.
     */
    private createSelectedOption(dropdownAction: IMatchWithObjectsOption, config: IMatchConfig): ISelectedOption {
        const {
            fieldName,
            label,
            subLabel,
            objectType,
            componentBindings = {},
        } = dropdownAction;

        const option = {
            ...dropdownAction,
        };

        option.componentBindings = {
            label,
            subLabel,
            objectType,
            fieldName,
            matchObjectType: this.getMatchComponentObjectType(objectType, fieldName),
            onRemoveMatch: () => {
                const selectedOptionIndex = this.selectedOptions.indexOf(option as ISelectedOption);

                this.removeMatch(fieldName, config, selectedOptionIndex);
            },
            config,
            ...componentBindings,
        };

        return option as ISelectedOption;
    }

    /**
     * Add an option to the list of selectedOptions to be rendered.
     */
    private addSelectedOption(dropdownAction: IMatchWithObjectsOption, matchConfig: IMatchConfig): void {
        const selectedComponent = this.createSelectedOption(dropdownAction, matchConfig);

        this.selectedOptions = this.selectedOptions.concat(selectedComponent);
    }

    /**
     * Add all selected options from existing configured matches.
     */
    private setSelectedOptions(): void {
        const selectedOptions: ISelectedOption[] = [];

        if (!this.match) {
            return;
        }

        this.matchOptions.forEach((matchOption: IMatchWithObjectsOption): void => {
            const { fieldName } = matchOption;

            if (Object.prototype.hasOwnProperty.call(this.match, fieldName)) {
                if (this.isRepeatedMatch(fieldName)) {
                    this.match[fieldName].forEach((matchConfig: IMatchConfig) => {
                        selectedOptions.push(this.createSelectedOption(matchOption, matchConfig));
                    });
                } else {
                    selectedOptions.push(this.createSelectedOption(matchOption, this.match[fieldName]));
                }
            }
        });

        this.selectedOptions = selectedOptions;
    }

    /**
     * Return true if a field is a repeated match.
     */
    private isRepeatedMatch(fieldName: string): boolean {
        return Boolean(this.getMatchMessageFields(this.matchObjectType, fieldName).isRepeated);
    }

    /**
     * Return the default config for child matches.
     */
    private getDefaultMatchConfig(fieldName: string, matchOption: IMatchWithObjectsOption): IMatchConfig {
        const { defaultConfigOverride = {} } = matchOption;
        const { objectType } = this.getMatchMessageFields(this.matchObjectType, fieldName);
        const defaultConfig = this.defaultValuesService.getDefaultItemConfigByType(objectType.toLowerCase()) || {};

        return {
            ...defaultConfig,
            ...defaultConfigOverride,
        };
    }

    /**
     * Return schema field properties for an objectType.
     */
    private getMatchMessageFields(objectType: string, fieldName: string): { objectType: string; isRepeated: boolean } {
        const fields = this.schemaService.getMessageFields(objectType);

        return fields[fieldName];
    }

    /**
     * Return the objectType for a child match config.
     */
    private getMatchComponentObjectType(parentObjectType: string, fieldName: string): string {
        const { objectType } = this.getMatchMessageFields(parentObjectType, fieldName);

        return objectType || '';
    }
}
